import { ReactNode, useMemo, useState } from "react";
import { HeadCell } from "@/components/DataTable/dataTable.interface";
import { Box, Tooltip } from "@mui/material";
import {
  Enrollment,
  Transaction,
  TransactionType,
} from "@/domain/enrollment.interface";
import { createDataRow } from "@/components/DataTable/createDataRow";
import DataTable from "@/components/DataTable";
import { MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { ACTION_CODE } from "@/domain/auditlog.enum";
import { useAuditLogFirstRender } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { formatCurrency, getLocaleByCountry } from "@/utils/formatters";
import { useCurrencySetting } from "@/hooks/useSetting";
import { useCountrySetting } from "@/hooks/useSetting";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

type IncentiveHistoryTabProps = {
  enrollment: Enrollment;
  programName: string;
  participantName: string;
};

type TransactionIncentive = Transaction & {
  provider: string;
};

const IncentiveHistoryTab: React.FC<IncentiveHistoryTabProps> = ({
  enrollment,
  participantName,
  programName,
}) => {
  const { t } = useTranslation("clients");
  const { userInfo } = useUserStore();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("date");

  const countrySetting = useCountrySetting();
  const currency = useCurrencySetting();
  const country = useMemo(() => countrySetting, [countrySetting]);
  const locale = useMemo(() => getLocaleByCountry(country), [country]);

  const transactions: TransactionIncentive[] = useMemo(() => {
    return (enrollment?.balance?.transactions || []).map((it) => {
      const provider =
        it.type === TransactionType.REDEEMED ? "Participant" : it.createdByName;
      return { ...it, provider: provider ?? "" };
    });
  }, [enrollment]);

  const sortedTransactions = useMemo(() => {
    return sortTransactions(transactions, orderBy, order);
  }, [transactions, orderBy, order]);

  const paginatedTransactions = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedTransactions.slice(start, end);
  }, [sortedTransactions, page, rowsPerPage]);

  const rows = useMemo(() => {
    return paginatedTransactions.map((transaction: TransactionIncentive) => {
      const timestamp = formatInBackofficeLondon(
        transaction.date,
        "DD/MM/YYYY hh:mma",
      );

      const record: Record<string, ReactNode> = {
        date: <div>{timestamp}</div>,
        type: (
          <div>
            {(t as any)(`transaction_type.${transaction.type}`)}
            {transaction?.reward?.vendorCard?.productName && (
              <Tooltip
                title={transaction.reward.vendorCard.productName}
                placement="right"
              >
                <InfoOutlinedIcon
                  style={{
                    fontSize: "1.25rem",
                    color: "#1F2626",
                  }}
                  color="primary"
                />
              </Tooltip>
            )}
          </div>
        ),
        amount: (
          <div
            style={{ color: transaction.amount < 0 ? "#AB0707" : "#1F2626" }}
          >
            {formatCurrency(
              transaction.amount,
              currency.currency,
              locale,
              2,
              true,
            )}
          </div>
        ),
        provider: <div>{transaction.provider}</div>,
      };
      const dataRow = createDataRow(transaction._id, record);
      dataRow.cellStyles = {
        amount: { paddingRight: "100px" },
      };
      return dataRow;
    });
  }, [paginatedTransactions, currency, locale, t]);

  const headCells = useMemo(() => {
    const baseHeadCells: HeadCell[] = [
      {
        id: "date",
        isSortable: true,
        disablePadding: true,
        label: "Timestamp",
        align: "left",
        width: "220px",
      },
      {
        id: "type",
        isSortable: true,
        disablePadding: true,
        label: "Transaction",
        align: "left",
        width: "220px",
      },
      {
        id: "amount",
        isSortable: true,
        disablePadding: true,
        label: "Value",
        align: "right",
        width: "200px",
        cellStyles: {
          paddingRight: "100px",
        },
      },
      {
        id: "provider",
        isSortable: true,
        disablePadding: true,
        label: "Provider",
        align: "left",
      },
    ];

    return baseHeadCells;
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeSort = (orderNew: any, orderByNew: any) => {
    setOrder(orderNew);
    setOrderBy(orderByNew);
    setPage(0);
  };

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ENROLLMENTS,
    option: OPTIONS.INCENTIVES_HISTORY,
    actionCode: ACTION_CODE.WEB_ENR_PART_DETAIL_INC,
    action: "When viewing an enrollment detail - Incentive history",
    detail: `Viewed enrollment detail - incentive history ${programName} for participant ${participantName}`,
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
  });

  return (
    <Box>
      <DataTable
        data={rows}
        headCells={headCells}
        rowsPerPage={rowsPerPage}
        page={page}
        total={transactions?.length || 0}
        order={order}
        orderBy={orderBy}
        onChangePage={handleChangePage}
        onChangeSize={handleChangeRowsPerPage}
        onChangeSort={handleChangeSort}
      />
    </Box>
  );
};
const sortTransactions = (
  data: TransactionIncentive[],
  orderBy: string,
  order: "asc" | "desc",
) => {
  return [...data].sort((a, b) => {
    let aValue: any = a[orderBy as keyof TransactionIncentive];
    let bValue: any = b[orderBy as keyof TransactionIncentive];

    if (orderBy === "timestamp" || orderBy === "date") {
      aValue = new Date(a.date).getTime();
      bValue = new Date(b.date).getTime();
    }

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });
};
export default IncentiveHistoryTab;
